import React, { useMemo, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import styles from './BodySilhouette.module.css';
import { BODY_SILHOUETTE_VIEWBOX, getBodySilhouetteFigure, spreadLabels } from './bodySilhouetteFigures';

const { width: VB_WIDTH, height: VB_HEIGHT } = BODY_SILHOUETTE_VIEWBOX;
const LEFT_EDGE = 8;
const RIGHT_EDGE = VB_WIDTH - 8;
const BEND = 30;
const DOT_RADIUS = 7;

const MARKER_TYPE_CLASS = {
  complaint: styles.markerComplaint,
  observation: styles.markerObservation,
  assessed: styles.markerAssessed,
};

const HALO_TYPE_CLASS = {
  complaint: styles.haloComplaint,
  observation: styles.haloObservation,
  assessed: styles.haloAssessed,
};

// Halo de intensidade: cresce e fica mais forte de 0 a 10
const HALO_MIN_SIZE = 24;
const HALO_STEP = 7;
const HALO_MAX_OPACITY = 0.75;

// Deslocamento mínimo (px) para um toque no marcador virar arrasto
const DRAG_THRESHOLD = 4;

const pct = (fraction) => `${fraction * 100}%`;
const clamp01 = (value) => Math.min(1, Math.max(0, value));

/**
 * Silhueta do paciente, de frente ou de costas, com dois usos:
 *
 * - etiquetas de medida ligadas ao corpo por linhas-guia (`labels`:
 *   [{ key, side: 'left' | 'right', title, value, note, preview }], `key` é
 *   uma região de `bodySilhouetteFigures.js`; etiquetas sem região são
 *   ignoradas; `preview` (nó opcional) aparece em um balão ao passar o mouse
 *   e, com `onLabelClick(key)`, as etiquetas viram botões);
 * - marcadores numerados sobre a figura (`markers`: [{ key, x, y, number,
 *   type, intensity, muted, selected, highlighted, title, details }], x e y
 *   como frações do quadro, 0 a 1; `intensity` de 0 a 10 desenha um halo em
 *   gradiente atrás do número; `title` e `details` (linhas) formam o balão
 *   mostrado ao passar o mouse; `highlighted` destaca o marcador por fora,
 *   por exemplo quando o item correspondente da lista está sob o mouse).
 *
 * `onPick({ x, y })` torna a figura clicável e recebe o ponto clicado em
 * frações do quadro; `pickLabel({ x, y })` devolve o texto mostrado junto ao
 * cursor antes do clique; `onMarkerClick(key)` atende ao clique em um marcador;
 * `onMarkerMove(key, { x, y })` permite arrastar os marcadores não esmaecidos
 * e recebe o ponto onde foram soltos; `onMarkerHover(key | null)` avisa qual
 * marcador está sob o mouse.
 * `variant`: 'male' | 'female' | 'neutral' (veja `variantFromGender`).
 * Em telas estreitas só a figura aparece: quem usa o componente lista as
 * medidas de outro jeito.
 */
const BodySilhouette = ({
  variant = 'neutral',
  view = 'front',
  labels = [],
  markers = [],
  onLabelClick,
  onPick,
  pickLabel,
  onMarkerClick,
  onMarkerMove,
  onMarkerHover,
  compact = false,
  large = false,
}) => {
  const figure = getBodySilhouetteFigure(variant);
  const src = view === 'back' ? figure.backSrc : figure.src;
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const placed = useMemo(() => {
    const withPoint = labels
      .map((label) => ({ ...label, ...(figure.regions[label.key] || {}) }))
      .filter((label) => Number.isFinite(label.x) && Number.isFinite(label.y));
    return {
      left: spreadLabels(withPoint.filter((label) => label.side === 'left')),
      right: spreadLabels(withPoint.filter((label) => label.side !== 'left')),
    };
  }, [labels, figure]);

  const leaders = [...placed.left, ...placed.right];

  function pointInRect(event, rect) {
    if (!rect.width || !rect.height) return null;
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  }

  function pointFromEvent(event) {
    const point = pointInRect(event, event.currentTarget.getBoundingClientRect());
    if (!point || point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1) return null;
    return point;
  }

  function handlePick(event) {
    if (!onPick) return;
    const point = pointFromEvent(event);
    if (point) onPick(point);
  }

  function handleMove(event) {
    if (!onPick || !pickLabel) return;
    const point = pointFromEvent(event);
    setHover(point ? { ...point, label: pickLabel(point) } : null);
  }

  // Arrasto de marcador: só vira arrasto depois de um deslocamento mínimo;
  // um toque curto continua sendo clique
  function dragPoint(event, current) {
    const point = pointInRect(event, current.rect);
    return point ? { x: clamp01(point.x), y: clamp01(point.y) } : null;
  }

  function startDrag(event, marker) {
    if (!svgRef.current || event.button !== 0) return;
    dragRef.current = {
      key: marker.key,
      startX: event.clientX,
      startY: event.clientY,
      rect: svgRef.current.getBoundingClientRect(),
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveDrag(event) {
    const current = dragRef.current;
    if (!current) return;
    if (!current.moved && Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < DRAG_THRESHOLD) return;
    current.moved = true;
    const point = dragPoint(event, current);
    if (!point) return;
    setDrag({ key: current.key, ...point });
    if (pickLabel) setHover({ ...point, label: pickLabel(point) });
  }

  function endDrag(event, marker) {
    const current = dragRef.current;
    if (!current) return;
    dragRef.current = null;
    setDrag(null);
    setHover(null);
    if (!current.moved) {
      if (onMarkerClick) onMarkerClick(marker.key);
      return;
    }
    const point = dragPoint(event, current);
    if (point) onMarkerMove(current.key, point);
  }

  function cancelDrag() {
    dragRef.current = null;
    setDrag(null);
    setHover(null);
  }

  function setMarkerHover(key) {
    setHoverKey(key);
    if (onMarkerHover) onMarkerHover(key);
  }

  const renderColumn = (items, side) => (
    <div className={styles.column}>
      {items.map((item) => {
        const clickable = Boolean(onLabelClick);
        const label = (
          <div
            className={[
              styles.label,
              side === 'left' ? styles.labelLeft : styles.labelRight,
              clickable ? styles.labelClickable : '',
            ].join(' ')}
            style={{ top: `${(item.labelY / VB_HEIGHT) * 100}%` }}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onLabelClick(item.key) : undefined}
            onKeyDown={clickable ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onLabelClick(item.key); } } : undefined}
          >
            <span className={styles.labelTitle}>{item.title}</span>
            <span className={styles.labelRow}>
              <span className={styles.labelValue}>{item.value}</span>
              {item.note && <span className={styles.labelDelta}>{item.note}</span>}
            </span>
          </div>
        );
        if (!item.preview) return <React.Fragment key={item.key}>{label}</React.Fragment>;
        return (
          <Tooltip
            key={item.key}
            title={item.preview}
            placement={side === 'left' ? 'left' : 'right'}
            enterDelay={300}
            componentsProps={{ tooltip: { className: styles.labelTip } }}
          >
            {label}
          </Tooltip>
        );
      })}
    </div>
  );

  const figureClass = [
    styles.figure,
    compact ? styles.figureCompact : '',
    large ? styles.figureLarge : '',
    onPick ? styles.figureInteractive : '',
  ].join(' ');

  // Posição exibida: a do arrasto em curso, se houver
  const positionOf = (marker) => (drag && drag.key === marker.key ? drag : marker);
  const halos = markers.filter((marker) => !marker.muted && Number.isFinite(marker.intensity) && marker.intensity > 0);
  // Balão do marcador sob o mouse; some durante o arrasto
  const tip = hoverKey && !drag ? markers.find((marker) => marker.key === hoverKey) : null;

  return (
    <div className={styles.wrap}>
      {renderColumn(placed.left, 'left')}

      <div className={styles.figureBox}>
        <svg
          ref={svgRef}
          className={figureClass}
          viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
          role={onPick ? 'button' : 'img'}
          aria-label={onPick ? 'Silhueta do paciente: clique para marcar um ponto' : 'Silhueta do paciente'}
          onClick={handlePick}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          <image className={styles.image} href={src} x={0} y={0} width={VB_WIDTH} height={VB_HEIGHT} />

          {leaders.map((item) => {
            const edge = item.side === 'left' ? LEFT_EDGE : RIGHT_EDGE;
            const bend = item.side === 'left' ? item.x - BEND : item.x + BEND;
            return (
              <g key={item.key}>
                <polyline className={styles.leader} points={`${item.x},${item.y} ${bend},${item.labelY} ${edge},${item.labelY}`} />
                <circle className={styles.dot} cx={item.x} cy={item.y} r={DOT_RADIUS} />
              </g>
            );
          })}
        </svg>

        {halos.map((marker) => {
          const size = HALO_MIN_SIZE + marker.intensity * HALO_STEP;
          const position = positionOf(marker);
          return (
            <span
              key={`halo-${marker.key}`}
              className={`${styles.halo} ${HALO_TYPE_CLASS[marker.type] || styles.haloObservation}`}
              style={{
                left: pct(position.x),
                top: pct(position.y),
                width: size,
                height: size,
                opacity: (marker.intensity / 10) * HALO_MAX_OPACITY,
              }}
            />
          );
        })}

        {markers.map((marker) => {
          const draggable = Boolean(onMarkerMove) && !marker.muted;
          const dragging = Boolean(drag) && drag.key === marker.key;
          const position = positionOf(marker);
          return (
            <button
              key={marker.key}
              type="button"
              className={[
                styles.marker,
                MARKER_TYPE_CLASS[marker.type] || styles.markerObservation,
                marker.muted ? styles.markerMuted : '',
                marker.selected ? styles.markerSelected : '',
                marker.highlighted && !marker.selected ? styles.markerHighlighted : '',
                draggable ? styles.markerDraggable : '',
                dragging ? styles.markerDragging : '',
              ].join(' ')}
              style={{ left: pct(position.x), top: pct(position.y) }}
              aria-label={marker.title || `Marcador ${marker.number}`}
              onMouseEnter={() => setMarkerHover(marker.key)}
              onMouseLeave={() => setMarkerHover(null)}
              onClick={draggable ? undefined : () => onMarkerClick && onMarkerClick(marker.key)}
              onKeyDown={draggable ? (event) => { if ((event.key === 'Enter' || event.key === ' ') && onMarkerClick) { event.preventDefault(); onMarkerClick(marker.key); } } : undefined}
              onPointerDown={draggable ? (event) => startDrag(event, marker) : undefined}
              onPointerMove={draggable ? moveDrag : undefined}
              onPointerUp={draggable ? (event) => endDrag(event, marker) : undefined}
              onPointerCancel={draggable ? cancelDrag : undefined}
            >
              {marker.muted ? '' : marker.number}
            </button>
          );
        })}

        {tip && tip.title && (
          <div
            className={`${styles.markerTip} ${tip.y < 0.14 ? styles.markerTipBelow : ''}`}
            style={{ left: pct(tip.x), top: pct(tip.y) }}
          >
            <span className={styles.markerTipTitle}>{tip.title}</span>
            {(tip.details || []).map((line, index) => (
              <span key={index} className={styles.markerTipLine}>{line}</span>
            ))}
          </div>
        )}

        {hover && hover.label && !tip && (
          <span className={styles.cursorLabel} style={{ left: pct(hover.x), top: pct(hover.y) }}>
            {hover.label}
          </span>
        )}
      </div>

      {renderColumn(placed.right, 'right')}
    </div>
  );
};

export default BodySilhouette;
